/** Withdraws current memberships behind a required reason; the request is revision- and evidence-guarded. */
import { HttpErrorResponse } from '@angular/common/http';
import { Injector, WritableSignal, runInInjectionContext, signal } from '@angular/core';
import { ActivityEntityName, MetadataEntityFieldEditorType, MetadataEntityFieldType, SurfaceType, VisitApprovalCommandId, VisitApprovalMembership, VisitApprovalRequestResult, VisitApprovalScope, VisitApprovalVisitRow, VisitApprovalWithdrawRequest } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { injectDialog } from '@workbuddy/ui/v2/blocks/dialog';
import { defineSurface, defineSurfaceSchema, openFormSurfaceDialog } from '@workbuddy/ui/v2/surface.engine';
import { ApprovalVisitsBoardParams, VisitApprovalRecordRef } from '../visit.approval.detail.model';
import { reloadApproval } from './approval.command.support';

interface ApprovalRemovalForm { reason: string; }

/** One approval's share of a removal: a Withdraw request targets exactly one approval. */
export interface ApprovalWithdrawalGroup {
  readonly approval: Pick<VisitApprovalRecordRef, '_id' | 'reviewContext' | 'currentRevision'>;
  readonly scope: VisitApprovalScope;
  readonly memberships: readonly Pick<VisitApprovalMembership, 'occurrenceId' | 'evidence'>[];
}

export interface ApprovalWithdrawalOptions {
  readonly groups: readonly ApprovalWithdrawalGroup[];
  /** Re-checked on submit: the host may lock while the prompt is open. */
  readonly blocked?: () => boolean;
  /** A mount-owned lock so a second removal cannot start while one commits; one per prompt otherwise. */
  readonly busy?: WritableSignal<boolean>;
  /** Rereads whatever quoted the withdrawn evidence; also runs after a conflict. */
  readonly reload: () => Promise<void>;
}

/** The server caps one withdrawal at 50 memberships. */
export const MAX_APPROVAL_REMOVALS = 50;

const CONFLICT_MESSAGE = 'This approval changed and has been reloaded. Your reason is kept; cancel and retry from the updated list.';

export async function removeFromApproval(injector: Injector, params: ApprovalVisitsBoardParams, rows: readonly VisitApprovalVisitRow[]): Promise<boolean> {

  if (!params.canRemove || params.busy() || !rows.length) return false;
  const memberships = rows.flatMap(row => row.membership ? [row.membership] : []);
  // A historical row may carry no membership: withdrawing it would target a decision already superseded.
  if (memberships.length !== rows.length) return false;
  return withdrawFromApprovals(injector, {
    groups: [{ approval: params.approval, scope: params.scope, memberships }],
    blocked: () => !params.canRemove,
    busy: params.busy,
    reload: () => reloadApproval(injector, params),
  });
}

/** The one reason prompt and Withdraw loop behind every "Remove from approval…", run once per approval. */
export async function withdrawFromApprovals(injector: Injector, options: ApprovalWithdrawalOptions): Promise<boolean> {

  const { groups } = options;
  const busy = options.busy ?? signal(false);
  const total = groups.reduce((sum, group) => sum + group.memberships.length, 0);
  if (!total || busy() || options.blocked?.() || groups.some(group => !group.memberships.length || group.memberships.length > MAX_APPROVAL_REMOVALS)) return false;
  const data = injector.get(EntityDataService);
  const dialogs = runInInjectionContext(injector, injectDialog);
  // Kept across retries in one dialog so a lost response replays the same idempotent request id.
  const requests = new Map<number, VisitApprovalWithdrawRequest>();
  // A committed approval is never resubmitted when a sibling approval in the batch failed.
  const committed = new Set<number>();
  // Per dialog: the reloaded board carries fresh evidence, so the next dialog may retry.
  let conflict = false;
  const result = await openFormSurfaceDialog<boolean>(dialogs, {
    title: `Remove ${total === 1 ? 'visit' : `${total} visits`} from approval`,
    createDefinition: api => defineSurface<ApprovalRemovalForm>({
      schema: defineSurfaceSchema(ActivityEntityName.VisitApproval, SurfaceType.Form)
        .model<ApprovalRemovalForm>()
        .submitLabel('Remove from approval')
        .body(body => body.section('reason', section => section.columns(1).field({ bind: 'reason', required: true, metadata: { type: MetadataEntityFieldType.String, editor: MetadataEntityFieldEditorType.TextArea, displayName: 'Reason for removal' } })))
        .build(),
      initialModel: { reason: '' },
      onCancel: () => api.dismiss(),
      onSubmit: async event => {
        if (options.blocked?.() || busy()) throw new Error('This approval is read-only in this context.');
        const reason = event.model.reason.trim();
        if (!reason) throw new Error('Enter a reason for removal.');
        if (conflict) throw new Error(CONFLICT_MESSAGE);
        const failures: string[] = [];
        let changed = false;
        busy.set(true);
        try {
          for (const [index, group] of groups.entries()) {
            if (committed.has(index)) continue;
            const previous = requests.get(index);
            // A changed reason is a different request; reusing the id would replay the first reason.
            const request: VisitApprovalWithdrawRequest = previous && previous.visits.every(visit => visit.reason === reason) ? previous : {
              requestId: crypto.randomUUID(),
              scope: group.scope,
              context: group.approval.reviewContext,
              target: { kind: 'Existing', approvalId: group.approval._id, initialExpectedRevision: group.approval.currentRevision },
              visits: group.memberships.map(membership => ({ occurrenceId: membership.occurrenceId, expectedEvidence: membership.evidence, reason })),
            };
            requests.set(index, request);
            try {
              const outcome = await data.executeCommand<VisitApprovalRequestResult>(ActivityEntityName.VisitApproval, VisitApprovalCommandId.Withdraw, request);
              if (outcome.kind === 'Pending') {
                failures.push('Removal is still processing. Check the saved operation in your inbox.');
                continue;
              }
              committed.add(index);
              changed = true;
            } catch (error) {
              const conflicted = error instanceof HttpErrorResponse && error.status === 409;
              conflict ||= conflicted;
              failures.push(conflicted
                ? CONFLICT_MESSAGE
                : error instanceof Error ? error.message : 'Removal could not be confirmed. Retry with the same reason.');
            }
          }
          if (!failures.length) {
            await options.reload();
            api.close(true);
            return event.model;
          }
          // Reload now rather than behind a banner: the rows under this dialog hold stale evidence.
          if (changed || conflict) void options.reload().catch(() => {});
        } finally {
          busy.set(false);
        }
        if (groups.length === 1) throw new Error(failures[0]);
        const removed = groups.reduce((sum, group, index) => sum + (committed.has(index) ? group.memberships.length : 0), 0);
        throw new Error(`Removed ${removed} of ${total} visits. ${failures.length} of ${groups.length} approvals could not be updated: ${failures[0]}`);
      },
    }),
  });
  return result === true;
}
